// Variant Gallery
// Reads variant image lists from the JSON block output by snippets/custom-variant-gallery.liquid
// (rendered inside sections/main-product.liquid) and updates the main viewer, thumbnails,
// and slide counter whenever the user selects a different variant.
(function () {
  let activeVariantId = null;
  let activeGalleries = null;
  let debounce = null;
  let observing = false;

  function syncThumbActive(thumbsList, idx) {
    thumbsList.querySelectorAll('.vg-thumb .media-thumbs__btn').forEach((btn, bi) => {
      btn.classList.toggle('is-active', bi === idx);
      if (bi === idx) {
        btn.setAttribute('aria-current', 'true');
      } else {
        btn.removeAttribute('aria-current');
      }
    });
  }

  function doInject(productMedia) {
    if (!activeVariantId || !activeGalleries) return;
    const images = activeGalleries[String(activeVariantId)];
    if (!images || !images.length) return;

    const viewer = productMedia.querySelector('ul.media-viewer');
    if (!viewer) return;

    // Guard: skip if already injected for this variant (prevents observer loop)
    const first = viewer.querySelector('.vg-item');
    if (first && first.dataset.vgVariant === String(activeVariantId)) return;

    // ── Main viewer ──────────────────────────────────────────────────────
    viewer.querySelectorAll('.vg-item').forEach((el) => el.remove());
    viewer.querySelectorAll('.vg-orig-hidden').forEach((el) => {
      el.classList.remove('vg-orig-hidden');
      el.style.display = '';
    });

    viewer.querySelectorAll('.media-viewer__item').forEach((el) => {
      el.classList.add('vg-orig-hidden');
      el.style.display = 'none';
    });

    [...images].reverse().forEach((url, ri) => {
      const i = images.length - 1 - ri;
      const src = url.startsWith('//') ? 'https:' + url : url;
      const li = document.createElement('li');
      li.className = 'media-viewer__item vg-item' + (i === 0 ? ' is-active' : '');
      li.dataset.vgVariant = String(activeVariantId);
      li.dataset.vgIndex = String(i);
      li.style.cssText = 'min-width:100%;flex-shrink:0;';
      li.innerHTML = '<img src="' + src + '" alt="" loading="' +
        (i === 0 ? 'eager' : 'lazy') + '" style="width:100%;height:auto;display:block;">';
      viewer.prepend(li);
    });

    viewer.scrollLeft = 0;

    // ── Slide counter ────────────────────────────────────────────────────
    const counterTotal = productMedia.querySelector('.media-ctrl__total-items');
    const counterCurrent = productMedia.querySelector('.media-ctrl__current-item');
    if (counterTotal) {
      if (!counterTotal.dataset.vgOrig) counterTotal.dataset.vgOrig = counterTotal.textContent;
      counterTotal.textContent = images.length;
    }
    if (counterCurrent) counterCurrent.textContent = '1';

    // ── Thumbnails ───────────────────────────────────────────────────────
    const thumbsList = productMedia.querySelector('ul.media-thumbs');
    if (thumbsList) {
      thumbsList.querySelectorAll('.vg-thumb').forEach((el) => el.remove());
      thumbsList.querySelectorAll('.vg-thumb-hidden').forEach((el) => {
        el.classList.remove('vg-thumb-hidden');
        el.style.display = '';
      });

      thumbsList.querySelectorAll('.media-thumbs__item').forEach((el) => {
        el.classList.add('vg-thumb-hidden');
        el.style.display = 'none';
      });

      images.forEach((url, i) => {
        const src = url.startsWith('//') ? 'https:' + url : url;
        const li = document.createElement('li');
        li.className = 'media-thumbs__item vg-thumb';
        li.dataset.vgIndex = String(i);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'media-thumbs__btn media relative w-full' + (i === 0 ? ' is-active' : '');
        if (i === 0) btn.setAttribute('aria-current', 'true');
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.style.cssText = 'width:100%;height:auto;display:block;';
        btn.appendChild(img);
        li.appendChild(btn);
        thumbsList.appendChild(li);
      });

      // Thumb click → scroll viewer to that image
      if (!thumbsList.dataset.vgWired) {
        thumbsList.dataset.vgWired = '1';
        thumbsList.addEventListener('click', (e) => {
          const li = e.target.closest('.vg-thumb');
          if (!li) return;
          const idx = parseInt(li.dataset.vgIndex, 10);
          viewer.scrollTo({ left: idx * viewer.offsetWidth, behavior: 'smooth' });
          syncThumbActive(thumbsList, idx);
        });
      }

      // Viewer scroll → update active thumbnail + slide counter
      if (!viewer.dataset.vgScrollWired) {
        viewer.dataset.vgScrollWired = '1';
        viewer.addEventListener('scroll', () => {
          if (!viewer.querySelector('.vg-item')) return;
          const itemWidth = viewer.offsetWidth;
          if (!itemWidth) return;
          const idx = Math.round(viewer.scrollLeft / itemWidth);
          syncThumbActive(thumbsList, idx);
          const cur = productMedia.querySelector('.media-ctrl__current-item');
          if (cur) cur.textContent = idx + 1;
        }, { passive: true });
      }
    }

    // ── Prev/next buttons ────────────────────────────────────────────────
    const gallery = productMedia.querySelector('media-gallery');
    if (gallery && !gallery.dataset.vgNav) {
      gallery.dataset.vgNav = '1';
      gallery.addEventListener('click', (e) => {
        const btn = e.target.closest('.media-ctrl__btn');
        if (!btn || !viewer.querySelector('.vg-item')) return;
        const items = viewer.querySelectorAll('.vg-item');
        if (!items.length) return;
        e.stopImmediatePropagation();
        const itemWidth = viewer.offsetWidth;
        const current = Math.round(viewer.scrollLeft / itemWidth);
        const next = btn.name === 'next'
          ? Math.min(current + 1, items.length - 1)
          : Math.max(current - 1, 0);
        viewer.scrollTo({ left: next * itemWidth, behavior: 'smooth' });
      }, true);
    }
  }

  function watchProductMedia(productMedia) {
    if (observing) return;
    observing = true;
    const obs = new MutationObserver(() => {
      if (!activeVariantId) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => doInject(productMedia), 80);
    });
    obs.observe(productMedia, { childList: true, subtree: true });
  }

  document.addEventListener('on:variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    const form = event.detail.form;
    if (!form) return;
    const shopifySection = form.closest('.shopify-section');
    if (!shopifySection) return;

    const jsonEl = shopifySection.querySelector('[id^="variant-galleries-"]');
    if (!jsonEl) return;
    let galleries;
    try { galleries = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    const images = galleries[String(variant.id)];
    const productMedia = shopifySection.querySelector('#product-media');
    if (!productMedia) return;

    if (!images || !images.length) {
      activeVariantId = null;
      activeGalleries = null;
      const viewer = productMedia.querySelector('ul.media-viewer');
      if (viewer) {
        viewer.querySelectorAll('.vg-item').forEach((el) => el.remove());
        viewer.querySelectorAll('.vg-orig-hidden').forEach((el) => {
          el.classList.remove('vg-orig-hidden');
          el.style.display = '';
        });
      }
      const thumbsList = productMedia.querySelector('ul.media-thumbs');
      if (thumbsList) {
        thumbsList.querySelectorAll('.vg-thumb').forEach((el) => el.remove());
        thumbsList.querySelectorAll('.vg-thumb-hidden').forEach((el) => {
          el.classList.remove('vg-thumb-hidden');
          el.style.display = '';
        });
      }
      const counterTotal = productMedia.querySelector('.media-ctrl__total-items');
      if (counterTotal && counterTotal.dataset.vgOrig) {
        counterTotal.textContent = counterTotal.dataset.vgOrig;
        delete counterTotal.dataset.vgOrig;
      }
      const counterCurrent = productMedia.querySelector('.media-ctrl__current-item');
      if (counterCurrent) counterCurrent.textContent = '1';
      return;
    }

    activeVariantId = variant.id;
    activeGalleries = galleries;

    watchProductMedia(productMedia);
    doInject(productMedia);
  });
}());
